import { api } from '@/lib/api'

function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null
  const m = document.cookie.match('(?:^|; )' + name.replace(/([.$?*|{}()\[\]\\\/\+^])/g, '\\$1') + '=([^;]*)')
  return m ? decodeURIComponent(m[1]) : null
}

export interface LoginInput {
  email: string
  password: string
  totp?: string
}

// ---- Session (Django) flow helpers ----

export async function sessionCsrf(): Promise<string | null> {
  const opts: RequestInit = {
    method: 'GET',
    credentials: 'include',
    cache: 'no-store',
    headers: { 'Accept': 'application/json' },
  }
  let lastErr: any = null
  for (let i = 0; i < 3; i++) {
    try {
      const res = await fetch('/api/auth/csrf', opts)
      if (!res.ok) {
        lastErr = new Error(`csrf ${res.status}`)
      } else {
        return getCookie('csrftoken')
      }
    } catch (e) {
      lastErr = e
    }
    await new Promise(r => setTimeout(r, 150 * (i + 1)))
  }
  return getCookie('csrftoken')
}

export type MFAMethod = 'totp' | 'email'

export type SessionLoginResult = {
  requires_2fa: boolean
  has_2fa: boolean
  needs_enrollment?: boolean
  preauth?: string | null
  redirect?: string | null
  mfa_method?: MFAMethod | null
  methods?: MFAMethod[]
  forced_email_mfa?: boolean
}

export async function sessionLogin(username: string, password: string, recaptchaToken: string): Promise<SessionLoginResult> {
  const csrf = getCookie('csrftoken')
  const res = await fetch('/api/auth/login', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(csrf ? { 'X-CSRFToken': csrf } : {}),
    },
    body: JSON.stringify({ username, password, recaptcha_token: recaptchaToken }),
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(data?.detail || 'Login failed')
  try {
    if (data?.requires_2fa && data?.preauth && typeof window !== 'undefined') {
      sessionStorage.setItem('login2fa:preauth', String(data.preauth))
    }
  } catch {}
  return {
    requires_2fa: !!data?.requires_2fa,
    has_2fa: !!data?.has_2fa,
    needs_enrollment: !!data?.needs_enrollment,
    preauth: (data?.preauth ?? null) as string | null,
    redirect: (data?.redirect ?? null) as string | null,
    mfa_method: (data?.mfa_method ?? null) as MFAMethod | null,
    methods: Array.isArray(data?.methods) ? data.methods as MFAMethod[] : undefined,
    forced_email_mfa: !!data?.forced_email_mfa,
  }
}

export async function sendEmailMFA(
  preauth: string,
  purpose: 'enrollment' | 'login',
): Promise<{ destination: string; expires_in: number; resend_after: number }> {
  const { data } = await api.post('/api/auth/2fa/email/send', { preauth, purpose })
  return data
}

export async function verifyEmailMFA(
  preauth: string,
  purpose: 'enrollment' | 'login',
  code: string,
): Promise<void> {
  await api.post('/api/auth/2fa/email/verify', { preauth, purpose, code })
}

export async function sessionTotpVerify(code: string, username?: string | null, password?: string | null): Promise<void> {
  const csrf = getCookie('csrftoken')
  const res = await fetch('/api/auth/2fa/verify', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(csrf ? { 'X-CSRFToken': csrf } : {}),
    },
    body: JSON.stringify((() => {
      try {
        const preauth = typeof window !== 'undefined' ? sessionStorage.getItem('login2fa:preauth') : null
        if (preauth) return { code, preauth }
      } catch {}
      if (username && password) return { code, username, password }
      return { code }
    })()),
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new Error(data?.detail || 'Invalid code')
  }
}

export async function sessionMe(): Promise<any> {
  const res = await fetch('/api/auth/me/', { credentials: 'include' })
  if (!res.ok) throw new Error('Not authenticated')
  return res.json()
}

export interface TokenPair {
  access: string
  refresh: string
}

export async function login({ email, password, totp }: LoginInput): Promise<TokenPair> {
  if (!email || !password) {
    throw new Error('Missing credentials')
  }
  // Ensure stale tokens don't bleed into pre-login requests
  try {
    if (typeof window !== 'undefined') {
      localStorage.removeItem('auth.token')
      localStorage.removeItem('auth.refresh')
    }
  } catch {}
  const payload: any = { email, password }
  if (totp && totp.length === 6) payload.totp = totp
  const { data } = await api.post<TokenPair>('/api/auth/token/', payload)
  if (typeof window !== 'undefined') {
    localStorage.setItem('auth.token', data.access)
    localStorage.setItem('auth.refresh', data.refresh)
    localStorage.setItem('auth.email', email.toLowerCase())
  }
  return data
}

export async function googleLogin(idToken: string): Promise<TokenPair> {
  if (!idToken) {
    throw new Error('Missing Google token')
  }
  const { data } = await api.post<TokenPair>('/api/auth/google', { id_token: idToken })
  if (typeof window !== 'undefined') {
    localStorage.setItem('auth.token', data.access)
    localStorage.setItem('auth.refresh', data.refresh)
  }
  return data
}

export function logout() {
  if (typeof window !== 'undefined') {
    localStorage.removeItem('auth.token')
    localStorage.removeItem('auth.refresh')
  }
}
